import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ActivatedRouteSnapshot } from "@angular/router";
import { IJwtTokenResponse, IOAuth2Token } from "@auvious/common";

import { AuviousRtcService } from "../../../services/rtc.service";
import { User } from "../../User";
import { IUser } from "../../IUser";
import { IAuthenticationStrategy } from "../IAuthenticationStrategy";
import { OAuth2TokenHelper } from "../../OAuth2TokenHelper";
import { ITokenResponse } from "../../ITokenResponse";
import { OAuth2CodeGrantAuthenticationServiceOptions } from "../../OAuth2CodeGrantAuthenticationServiceOptions";
import {
  OAUTH2_STATE,
  KEY_REDIRECT_URI,
  AUTH_POPUP_MESSAGE_CODE,
  AUTH_POPUP_MESSAGE_ACK,
  KEY_AUTH_HANDOVER_PREFIX,
  isPopUpWindowName,
  PARAM_POPUP,
  PARAM_AUTH_ATTEMPT
} from "../../../core-ui.enums";
import { AuthState } from "../../AuthState";
import {
  AuthFlowCoordinator,
  AUTH_MAX_ATTEMPTS
} from "../../AuthFlowCoordinator";

import { delay } from "../../../services/utils";
import { UserRoleEnum } from "../../../core-ui.enums";
import { localStore, sessionStore } from "@auvious/utils";
import { Util } from "@auvious/common";
import { firstValueFrom } from "rxjs";
import { PublicParam } from "../../application-options";
import { PARAM_GUEST } from "../../../../app/app.enums";
import { NotificationService } from "../../../services";
import {
  ActivityIndicatorService,
  IActivityPrompt,
  ActivityPromptKind
} from "../../../services/activity-indicator.service";
import { ConfirmNotification } from "../../notifications";

/** how long the authentication popup waits for its opener to claim the authorization response */
const AUTH_HANDOVER_TIMEOUT_MS = 3000;

/** a hand-over marker older than this was left behind by a window that is long gone */
const AUTH_HANDOVER_MAX_AGE_MS = 10 * 60 * 1000;

/** how long the popup defers to a watching opener before deciding that nobody is coming */
const AUTH_HANDOVER_GRACE_MS = 30000;

/** how often the window watching a sign-in popup says that its flow is still going */
const AUTH_FLOW_RENEW_MS = 10000;

/**
 * has the popup come back to us carrying the identity provider's answer?
 *
 * a popup sits on an about:blank document of our own origin until the identity provider's
 * response commits, and an org with federated login walks through hosts of its own on the way,
 * so "not on the authorization host" does not mean the popup is done. the authorization
 * response is what we are waiting for, so that is what we look for.
 */
function isAuthorizationResponse(href: string): boolean {
  try {
    const url = new URL(href, window.location.origin);
    return (
      url.origin === window.location.origin &&
      (url.searchParams.has("code") || url.searchParams.has("error"))
    );
  } catch {
    return false;
  }
}

/**
 * is this the application window the agent popped a call out to?
 *
 * a popped-out window is a top-level window with a session storage of its own: what it got from
 * the frame that opened it is a copy, not a share. there is nothing in it to coordinate with, and
 * a flow marker copied over from that frame would only make it wait on a sign-in it can never
 * see finish. the window marks itself by name and by url parameter (see DeviceService.isPopUp),
 * since the window chrome is not a reliable tell.
 */
function isPoppedOutWindow(): boolean {
  try {
    return (
      isPopUpWindowName(window.name) ||
      new URLSearchParams(window.location.search).has(PARAM_POPUP)
    );
  } catch {
    return false;
  }
}

/**
 * were we opened by a window of our own origin? a cross-origin opener - the call centre's page
 * that launched us in a new tab - throws on reading its location, and could never be watching
 * for an authorization response of ours anyway.
 */
function hasSameOriginOpener(): boolean {
  try {
    return (
      !!window.opener &&
      window.opener !== window &&
      window.opener.location.origin === window.location.origin
    );
  } catch {
    return false;
  }
}

export class OAuth2CodeGrantAuthenticationStrategy
  implements IAuthenticationStrategy
{
  persistAccessToken: boolean;
  usePopOutAuthentication: boolean;

  /**
   * keeps the instances of the application that share this tab from each running a flow of their
   * own. a call centre can render us several times in one tab - genesys does with several
   * interaction widgets - and they all boot together.
   */
  private coordinator = new AuthFlowCoordinator();

  /**
   * the agent asked for one more go from the retry-limit dialog. the count this instance carries
   * on its url starts over on the navigation that follows, or the click could never get past it.
   */
  private attemptChainReset = false;

  constructor(
    private rtcService: AuviousRtcService,
    private httpClient: HttpClient,
    private notification: NotificationService,
    private activity: ActivityIndicatorService,
    private options: OAuth2CodeGrantAuthenticationServiceOptions
  ) {
    this.persistAccessToken =
      options.config.publicParameters?.[
        PublicParam.ACCESS_TOKEN_AGENT_SESSION_STORE_ENABLED
      ] ?? true;
    this.usePopOutAuthentication =
      options.config.publicParameters?.[
        PublicParam.POPUP_AUTHENTICATION_ENABLED
      ] ?? false;
  }

  /**
   * sign in through a popup, or take the whole window to the identity provider?
   *
   * the popup is there for when we are embedded: the identity provider's login page refuses to be
   * framed, so a redirect inside our frame goes nowhere. a window of our own - a call centre that
   * opens us in a new tab, as mobile office does, or the agent's popped-out call window - has no
   * frame to break, and a redirect there needs no pop-up permission and no hand-over between two
   * windows. on ios in particular that hand-over is what kept failing.
   */
  private shouldPopOut(): boolean {
    return this.usePopOutAuthentication && this.isFramed();
  }

  /** are we running inside a frame? a seam, so that the specs can say either way */
  protected isFramed(): boolean {
    try {
      return window.self !== window.top;
    } catch {
      // reading a cross-origin top can throw in older browsers, and only a frame has one
      return true;
    }
  }

  private isPopup() {
    return (
      window.opener && window.opener !== window && !window.menubar?.visible
    );
  }

  /**
   * are we the sign-in popup, carrying the response to a flow our opener started?
   *
   * the window chrome cannot be relied on: ios opens a popup as an ordinary tab, menu bar and
   * all, and a popup that took itself for an ordinary window redeemed the code while its opener
   * read the same code off its url, closed it mid-request and redeemed it again - a 401 every time
   * the popup won the race. the state we issued says it was for a popup, and only a window of our
   * own origin can have opened one. the agent's popped-out call window runs flows of its own, so
   * when it comes back from one it is that flow's opener, not its popup.
   */
  private isAuthPopup(state: string): boolean {
    if (this.isPopup()) {
      return true;
    }
    if (!state || isPoppedOutWindow() || !hasSameOriginOpener()) {
      return false;
    }
    return AuthState.fromSerializedState(state)?.popup === true;
  }

  /**
   * is the window that opened us still watching for *this* authorization response?
   *
   * the opener keeps a marker for as long as it watches its authentication popup, and local
   * storage is shared between the windows of an origin (session storage is not: a popup only gets
   * a copy of it). it is what tells an unanswered hand-over apart from a window that has an opener
   * but nobody waiting on it, so the two can be treated differently.
   *
   * the marker is keyed by authorization state, which is unique per flow. the agent can have the
   * application open in several windows, each running its own flow and watching its own popup, and
   * a popped-out application window can land carrying a code while another window is authenticating
   * - none of them may answer for one another.
   */
  private isHandOverPending(state: string): boolean {
    try {
      if (!state || !window.opener || window.opener.closed) {
        return false;
      }
      const startedAt = Number(
        localStore.getItem(KEY_AUTH_HANDOVER_PREFIX + state)
      );
      return !!startedAt && Date.now() - startedAt < AUTH_HANDOVER_MAX_AGE_MS;
    } catch {
      return false;
    }
  }

  /**
   * a window that navigates elsewhere never gets to clear its own marker, so drop the ones that
   * are too old to belong to a live flow before adding another.
   */
  private sweepHandOverMarkers() {
    try {
      const stale: string[] = [];
      for (let i = 0; i < localStore.length; i++) {
        const key = localStore.key(i);
        if (!key?.startsWith(KEY_AUTH_HANDOVER_PREFIX)) {
          continue;
        }
        const startedAt = Number(localStore.getItem(key));
        if (!startedAt || Date.now() - startedAt >= AUTH_HANDOVER_MAX_AGE_MS) {
          stale.push(key);
        }
      }
      stale.forEach((key) => localStore.removeItem(key));
    } catch {}
  }

  /**
   * ask the window that opened us whether it is waiting for an authorization response.
   * only a window that has just opened an authentication popup answers, which is what tells
   * the authentication popup apart from the agent's popped-out application window (the latter
   * has an opener too, but nobody there is waiting for a code, so it authenticates itself).
   *
   * resolves true only if the opener acknowledged and is taking over the code.
   */
  private handOverToOpener(state: string): Promise<boolean> {
    const opener: Window = window.opener;

    if (!opener) {
      return Promise.resolve(false);
    }

    return new Promise<boolean>((resolve) => {
      const origin = window.location.origin;

      const cleanup = () => {
        clearTimeout(timer);
        window.removeEventListener("message", onAck);
      };

      const onAck = (event: MessageEvent) => {
        if (
          event.origin !== origin ||
          event.data?.type !== AUTH_POPUP_MESSAGE_ACK ||
          // several flows can be in the air at once, each with a popup of its own. an
          // acknowledgement that is not about our response says nothing about ours.
          event.data?.state !== state
        ) {
          return;
        }
        cleanup();
        resolve(true);
      };

      const timer = setTimeout(() => {
        // nobody is waiting for us. carry on and authenticate in this window.
        cleanup();
        resolve(false);
      }, AUTH_HANDOVER_TIMEOUT_MS);

      window.addEventListener("message", onAck);

      try {
        opener.postMessage(
          { type: AUTH_POPUP_MESSAGE_CODE, href: window.location.href, state },
          origin
        );
      } catch {
        // cross-origin opener. the message is dropped, so there is nobody to hand over to.
        cleanup();
        resolve(false);
      }
    });
  }

  async authenticate(route: ActivatedRouteSnapshot): Promise<IUser> {
    const hasAuthorizationResponse =
      !!route.queryParams["code"] || !!route.queryParams["error"];

    if (
      hasAuthorizationResponse &&
      this.isAuthPopup(route.queryParams["state"])
    ) {
      if (await this.handOverToOpener(route.queryParams["state"])) {
        // the opener acknowledged: it took the authorization response and carries on from there.
        // the code is single-use, so this window must never redeem it.
        window.close();
        // never resolves. this window is going away, nothing else should run in it.
        return new Promise<IUser>(() => {});
      }

      if (this.isHandOverPending(route.queryParams["state"])) {
        // the opener never answered, but it is still watching us and also polls our location, so
        // it can read the response off our url instead - and it only gets to use it if we leave
        // the code alone. redeeming it here is exactly what makes the opener's turn fail.
        //
        // a watching opener closes us within a poll or two. still being here long after that means
        // nobody is coming: the marker outlived the window that wrote it (a navigation elsewhere
        // never gets to clear it) or it belongs to another tab. carry on rather than leave the
        // agent looking at a spinner for good.
        await delay(AUTH_HANDOVER_GRACE_MS);
      }
    }

    await this.options.beforeAuthenticate?.(route);
    const queryParams = new URLSearchParams(route.queryParams);

    const code = queryParams.get("code");
    const state = queryParams.get("state");
    const error = queryParams.get("error");
    const errorDescription = queryParams.get("error_description");

    if (error) {
      // avoid redirect-loop by checking error parameter that
      // genesys cloud sends in case of invalid scope
      throw new Error(`${error}: ${errorDescription}`);
    }

    const storedToken = OAuth2TokenHelper.getToken(code);

    if (
      !storedToken &&
      (!code || !state || !this.coordinator.isIssuedState(state))
    ) {
      // there is no authorization response to work with, but the last session may have left a
      // credential the token endpoint still takes. that costs no popup, so try it first.
      const reusedUser = await this.authenticateWithReusableCredential(queryParams);
      if (reusedUser) {
        return reusedUser;
      }

      return this.shouldPopOut()
        ? new Promise<IUser>((resolve, reject) =>
            this.startOrJoinFlow(queryParams).then(resolve, reject)
          )
        : // the redirect branch used to swallow its errors, so a throw from inside vanished
          // into an unhandled rejection and the spinner stayed forever. it rejects like the
          // popup branch does, and the guard turns that into the authentication-failure page.
          new Promise<IUser>((resolve, reject) =>
            this.redirectForCode(queryParams).catch(reject)
          );
    }

    return this.completeAuthentication(code, storedToken, queryParams);
  }

  /**
   * run the flow ourselves, or wait for the instance of the application that already is.
   *
   * only one instance per tab may ask the identity provider for a code at a time: they share one
   * session storage, so several flows at once take over one another's authorization response and
   * restart for ever. each instance still gets a code, and a token, of its own.
   */
  private async startOrJoinFlow(queryParams: URLSearchParams): Promise<IUser> {
    // storage that is blocked is per-frame, and a popped-out window only has a copy of the
    // frame's: in neither case is there anything to coordinate through, so the instance runs its
    // own flow, as it always did.
    if (this.coordinator.isStorageShared() && !isPoppedOutWindow()) {
      const claim = await this.coordinator.claimFlow();

      if (claim.role === "waiter") {
        // when it lets go of the flow we take our turn. the attempt budget below is what keeps
        // that from going round for ever should the flows keep failing.
        await this.coordinator.awaitFlowRelease();

        // the instance that held it may have signed in and left a credential behind. taking our
        // turn at the popup without looking would open one it has already made unnecessary.
        const reusedUser = await this.authenticateWithReusableCredential(queryParams);
        if (reusedUser) {
          return reusedUser;
        }

        return this.startOrJoinFlow(queryParams);
      }
    }

    return this.runFlow(queryParams);
  }

  private async runFlow(queryParams: URLSearchParams): Promise<IUser> {
    try {
      await this.popOutForCode(queryParams);
    } catch (ex) {
      this.coordinator.abandonFlow();
      throw ex;
    }
    // the flow continues on the document we navigate to once the response arrives, so nothing
    // else may run in this one.
    return new Promise<IUser>(() => {});
  }

  /**
   * turn the authorization response, or the token we already have, into a signed-in user.
   */
  private async completeAuthentication(
    code: string,
    storedToken: ITokenResponse,
    queryParams: URLSearchParams
  ): Promise<IUser> {
    const state = queryParams.get("state");
    let userToken: IOAuth2Token;

    try {
      userToken = await this.establishSession(code, storedToken, state);
    } catch (ex) {
      // the flow is over either way. let go of it now rather than have the other instances of
      // this tab sit out the rest of its lease. only ours: an instance that comes back on a
      // stored token is not the one running the flow, if any is running at all.
      this.coordinator.abandonFlow(state);
      throw ex;
    }

    return this.finishSession(userToken, queryParams);
  }

  /**
   * a session is established. put the tab back in order and turn the token into a user.
   *
   * shared with the reusable-credential path, which reaches a session without an authorization
   * response but leaves the tab in exactly the same state as one that went the long way round.
   */
  private async finishSession(
    userToken: IOAuth2Token,
    queryParams: URLSearchParams
  ): Promise<IUser> {
    const state = queryParams.get("state");

    // we have a session, so this tab's authorize attempts were not spent on a loop after all.
    // not gated on persistence: the full-page redirect flows keep no token but must not run out
    // of attempts either.
    this.coordinator.resetAttempts();
    // and let go of the flow, which is what tells the next instance of this tab to take its turn.
    this.coordinator.abandonFlow(state);
    // a prompt left over from an earlier round of this flow has nothing to offer any more.
    this.activity.clearPrompt();

    await this.options.afterAuthenticate?.();

    const user = new User(userToken);

    // check for guest agent
    if (queryParams.get(PARAM_GUEST) === "true") {
      user.addRole(UserRoleEnum.guestAgent);
    } else if (queryParams.has("state")) {
      const astate = AuthState.fromSerializedState(queryParams.get("state"));
      if (astate.guest) {
        user.addRole(UserRoleEnum.guestAgent);
      }
    }

    return user;
  }

  /**
   * sign in with the credential the last session left behind, skipping the identity provider.
   * resolves null when there was nothing to present or it was refused, and the caller carries on
   * with the authorization code flow as before.
   */
  private async authenticateWithReusableCredential(
    queryParams: URLSearchParams
  ): Promise<IUser | null> {
    const tokenResponse = await this.tryReusableCredential();

    if (!tokenResponse) {
      return null;
    }

    const auviousCommonClient =
      await this.rtcService.createAuviousCommonClient();

    const userToken = await auviousCommonClient.loginTemplate(
      () => Promise.resolve(tokenResponse),
      // there is no authorization code to key a stored token by. the credential itself is what
      // carries this session across a reload, so nothing needs to be kept under one.
      this.refreshTokenFn(undefined)
    );

    return this.finishSession(userToken, queryParams);
  }

  /**
   * present the reusable credential to the token endpoint.
   * resolves null when there is none, or when it was not accepted.
   */
  private async tryReusableCredential(): Promise<ITokenResponse | null> {
    const reusable = this.options.reusableCredential?.load();

    if (!reusable) {
      return null;
    }

    try {
      return await this.getAccessToken(undefined, reusable);
    } catch (ex) {
      // revoked, expired, or minted for something else. forget it rather than present it again
      // on the next reload, and let the caller sign in the long way.
      //
      // deliberately not an authorize attempt and not an error: nothing was asked of the identity
      // provider, and the agent still has the popup ahead of them.
      this.options.reusableCredential.clear();
      return null;
    }
  }

  /**
   * exchange the authorization code for a session, or carry on with the token we already have.
   */
  private async establishSession(
    code: string,
    storedToken: ITokenResponse,
    state: string
  ): Promise<IOAuth2Token> {
    const auviousCommonClient =
      await this.rtcService.createAuviousCommonClient();

    if (!storedToken) {
      sessionStore.removeItem(OAUTH2_STATE);
      this.coordinator.forgetIssuedState(state);

      return auviousCommonClient.loginTemplate(async () => {
        try {
          return await this.getAccessToken(code);
        } catch (ex) {
          throw ex;
          // WARN! this causes a redirect loop if the token auth goes wrong
          // probably coming from a refresh where we switched from session token to stateless token
          // return this.usePopOutAuthentication
          //   ? new Promise(() => this.popOutForCode(queryParams))
          //   : new Promise(() => this.redirectForCode(queryParams));
        }
      }, this.refreshTokenFn(code));
    }

    if (
      OAuth2TokenHelper.isTokenExpired(storedToken) &&
      (!storedToken.refresh_token ||
        OAuth2TokenHelper.isRefreshTokenExpired(storedToken))
    ) {
      // probably a refresh after a standby/suspend? the session we kept is gone, and the code on
      // the url was spent buying it - asking for it again is only ever answered with an error.
      // a credential from that session is the one thing that can still get us back in.
      const reused = await this.tryReusableCredential();
      if (reused) {
        return auviousCommonClient.loginTemplate(
          () => Promise.resolve(reused),
          this.refreshTokenFn(code)
        );
      }

      return auviousCommonClient.loginTemplate(
        async () => this.getAccessToken(code),
        this.refreshTokenFn(code)
      );
    }

    // probably a refresh. If we have a refresh token, let's try it now, because if it fails later, we're screwed
    if (
      !!storedToken.refresh_token &&
      !OAuth2TokenHelper.isRefreshTokenExpired(storedToken)
    ) {
      try {
        await this.refreshTokenFn(code)(storedToken.refresh_token);
      } catch (ex) {
        // the refresh token is single-use and short lived, so an agent coming back to a tab they
        // left for a while lands here. a credential from that session gets us straight back in;
        // the reload below is only for when there is none.
        const reused = await this.tryReusableCredential();
        if (reused) {
          return auviousCommonClient.loginTemplate(
            () => Promise.resolve(reused),
            this.refreshTokenFn(code)
          );
        }

        // refresh-token failed so delete the cached token and reload without code, so that we
        // login again.
        this.clearAuthState(code, state);

        const url = new URL(window.location.toString());
        url.searchParams.delete("code");
        window.location.replace(url.toString());
        await delay(5000);
        throw ex;
      }
      const newToken = OAuth2TokenHelper.getToken(code);
      return auviousCommonClient.loginTemplate(
        () => Promise.resolve(newToken),
        this.refreshTokenFn(code)
      );
    }

    return auviousCommonClient.loginTemplate(() => {
      this.options.onTokenResponse?.(storedToken);
      return Promise.resolve(storedToken);
    }, this.refreshTokenFn(code));
  }

  /**
   * construct a url with all the required authorization params
   */
  private async getAuthorizationUrl(
    queryParams: URLSearchParams,
    popup = false
  ): Promise<URL> {
    const authState = AuthState.fromQueryParamMap(queryParams);
    if (popup) {
      authState.markAsPopup();
    }
    const state = authState.serialize();
    sessionStore.setItem(OAUTH2_STATE, state);
    // one entry per flow, so that an instance of the application recognises its own response.
    // the single slot above is last-writer-wins between the instances of a tab.
    this.coordinator.registerIssuedState(state);

    this.storeRedirectPath();

    const authProvider = await this.options.authProvider();

    const url = new URL(authProvider.authorizationUrl);
    url.searchParams.append("response_type", "code");
    url.searchParams.append("state", state);
    url.searchParams.append("client_id", authProvider.clientId);
    if (authProvider.scope) {
      url.searchParams.append("scope", authProvider.scope);
    }
    url.searchParams.append("redirect_uri", this.getRedirectPath());

    if (!!authProvider.authorizationUrlExtraParameters) {
      for (const [key, value] of Object.entries(
        authProvider.authorizationUrlExtraParameters
      )) {
        url.searchParams.append(key, value);
      }
    }
    return url;
  }

  /**
   * open a window popup to the auth url and wait for the redirect to get the code and close the popup.
   */
  private async popOutForCode(queryParams: URLSearchParams): Promise<void> {
    if (
      !this.coordinator.hasAttemptsLeft() ||
      (!this.attemptChainReset && this.attemptsInUrl() >= AUTH_MAX_ATTEMPTS)
    ) {
      // asking again is what ends up answered with "too many redirects". let go of the flow, so
      // the other instances of this tab are not left waiting on a dialog.
      this.coordinator.abandonFlow();
      this.notifyTooManyAttempts(queryParams);
      return;
    }

    const authorizationUrl = await this.getAuthorizationUrl(queryParams, true);
    const flowId = Util.uuidgen(Date.now().toString());

    const popup = this.openAuthPopup(authorizationUrl, flowId);

    if (!popup) {
      // we let the user in a spinner but the url has not changed.
      // the user may allow popups and refresh the page, or retry from the notification.
      // if we threw an error instead, the user would go to our error page but the url would have
      // changed, so even if they refreshed, they would still be on the error page.
      //
      // not an attempt: a blocked window asked the identity provider nothing and navigates
      // nowhere, and the budget counts round trips that can loop. counting it would let three
      // blocked instances lock the rest of the tab out of signing in at all.
      this.coordinator.abandonFlow();
      this.promptPopupBlocked(authorizationUrl, flowId);
      return;
    }

    // the sign-in window is open: this round trip ends in a navigation, so it counts
    this.coordinator.consumeAttempt();
    this.coordinator.renewFlow(authorizationUrl.searchParams.get("state"));
    this.watchPopup(popup, authorizationUrl, flowId);
  }

  private openAuthPopup(authorizationUrl: URL, flowId: string): Window {
    // a constant name is shared across the whole browsing-context group: every same-origin frame
    // of this tab would find - and re-navigate - one another's sign-in window, and each of them
    // would then be watching a window that is serving somebody else's flow.
    return window.open(
      authorizationUrl.toString(),
      `auvious-auth-${flowId}`,
      "popup=true,width=400,height=600"
    );
  }

  private promptPopupBlocked(authorizationUrl: URL, flowId: string) {
    this.promptPopupInterrupted(
      authorizationUrl,
      flowId,
      "popup-blocked",
      "Allow pop-ups to sign in",
      "authentication-popup-blocked"
    );
  }

  /**
   * tell the user why authentication is not going anywhere and let them retry.
   * the retry runs on a click, so the browser allows the popup to open.
   *
   * it takes the spinner's place rather than sitting on top of it: a spinner that stays up while
   * nothing is happening is what had users waiting on a sign-in that was never coming.
   */
  private promptPopupInterrupted(
    authorizationUrl: URL,
    flowId: string,
    kind: ActivityPromptKind,
    title: string,
    body: string
  ) {
    const state = authorizationUrl.searchParams.get("state");
    const prompt: IActivityPrompt = {
      kind,
      title,
      body,
      retry: () => {
        // a retry is a click, and every round of it needs another one, so it can never loop on
        // its own. it deliberately stays outside the attempt budget: counting it turned this
        // dialog into a dead end whenever the other instances of the tab had spent the budget.
        //
        // the same flow, so the same window name: a new one would leave the old window orphaned
        const popup = this.openAuthPopup(authorizationUrl, flowId);
        if (!popup) {
          // blocked this time too. ask again.
          this.promptPopupBlocked(authorizationUrl, flowId);
          return;
        }
        this.coordinator.renewFlow(state);
        this.watchPopup(popup, authorizationUrl, flowId);
      }
    };
    this.activity.prompt(prompt);
  }

  /**
   * the tab has spent its authorize attempts. stop rather than ask the identity provider again -
   * that is what ends up answered with "too many redirects".
   */
  private notifyTooManyAttempts(queryParams: URLSearchParams) {
    const notification = new ConfirmNotification(
      "Sign-in could not be completed",
      // with storage blocked nothing we keep survives the navigation back from the identity
      // provider - not the state we issued, not the attempt budget - so the round trip fails
      // every time and only the count on the url stops it. say what the agent can do about it.
      this.coordinator.isStorageShared()
        ? "authentication-retry-limit"
        : "authentication-storage-blocked",
      // the agent has no other way forward, so they cannot dismiss or hide this one.
      { dismissible: false, confirmLabel: "Retry" }
    );
    notification.onConfirmed(() => {
      this.coordinator.resetAttempts();
      this.attemptChainReset = true;
      if (!this.shouldPopOut()) {
        this.redirectForCode(queryParams).catch(() => {});
        return;
      }
      // on a click, so the browser allows the popup to open
      this.startOrJoinFlow(queryParams).then(
        // the router stopped waiting on our promise long ago, so a session that resolves here
        // reaches nobody. reload, and let the guards pick it up.
        () => window.location.reload(),
        () => {}
      );
    });
    this.notification.notify(notification);
  }

  /**
   * wait for the popup to come back from the identity provider and continue on this window.
   *
   * the popup hands the authorization response over with a message and closes itself without
   * redeeming the code, since the code is single-use and this is the window that needs the session.
   * polling the popup's location stays as a fallback for when that message never arrives.
   */
  private watchPopup(popup: Window, authorizationUrl: URL, flowId: string) {
    let done = false;
    const expectedState = authorizationUrl.searchParams.get("state");
    const handOverKey = KEY_AUTH_HANDOVER_PREFIX + expectedState;

    /**
     * is this the response to the flow *this* window started?
     *
     * several instances of the application can be signing in at once, and a popup answers to
     * whichever of them is watching it. taking over a response of another flow means arriving
     * on a state we did not issue - so on somebody else's application and interaction ids - and
     * spending a single-use code its owner still needs.
     */
    const isOurResponse = (href: string) => {
      if (!isAuthorizationResponse(href)) {
        return false;
      }
      try {
        const url = new URL(href, window.location.origin);
        return url.searchParams.get("state") === expectedState;
      } catch {
        return false;
      }
    };

    // announce that this window is waiting for this authorization response, so the popup hands it
    // over instead of redeeming the single-use code itself when our acknowledgement goes missing.
    this.sweepHandOverMarkers();
    try {
      localStore.setItem(handOverKey, String(Date.now()));
    } catch {}

    const stopWatching = () => {
      clearInterval(popupDetectionTimer);
      window.removeEventListener("message", onPopupMessage);
      try {
        localStore.removeItem(handOverKey);
      } catch {}
    };

    /** we have the authorization response. continue the flow on this window. */
    const takeOver = async (href: string) => {
      if (done || !isOurResponse(href)) {
        return;
      }
      done = true;
      stopWatching();
      // we are about to navigate. hold the flow open across it, so that the other instances of
      // this tab keep waiting for the token instead of starting flows of their own. this is the
      // one gap the marker has to outlive its document for, and saying so is what tells it apart
      // from one an agent left behind by reloading.
      this.coordinator.renewFlow(expectedState, true);

      const url = new URL(href);
      const newUrl = new URL(this.options.redirectUri, window.location.origin);
      // pass everything the identity provider sent back on to ourselves
      url.searchParams.forEach((value, key) =>
        newUrl.searchParams.set(key, value)
      );
      // and count the trip. the document we navigate to reads it back, so a round trip that
      // keeps failing is bounded even where nothing else survives the navigation.
      newUrl.searchParams.set(
        PARAM_AUTH_ATTEMPT,
        String(this.attemptChainReset ? 1 : this.attemptsInUrl() + 1)
      );
      // I could not find a way to use window.history.replaceState within angular so as to avoid the refresh.
      // angular redirected back to the previous url because history.replaceState was happening outside angular.
      this.navigate(newUrl.toString());
      await delay(5000);
    };

    const onPopupMessage = (event: MessageEvent) => {
      if (
        event.origin !== window.location.origin ||
        event.source !== popup ||
        event.data?.type !== AUTH_POPUP_MESSAGE_CODE ||
        // never claim a response we cannot use: the popup would close itself and leave the flow
        // it belongs to with a code that has been handed to the wrong window.
        !isOurResponse(event.data.href)
      ) {
        return;
      }
      // acknowledge, so that the popup closes itself without using the code.
      // do not close it from here on this path, or the acknowledgement can be destroyed
      // before the popup gets to handle it.
      (event.source as Window).postMessage(
        { type: AUTH_POPUP_MESSAGE_ACK, state: expectedState },
        event.origin
      );
      takeOver(event.data.href);
    };

    window.addEventListener("message", onPopupMessage);

    let renewedAt = Date.now();

    const popupDetectionTimer = setInterval(() => {
      if (!popup) {
        return;
      }
      // the sign-in window is still open, so this flow is still going. hold it open for as long
      // as the agent needs - a password to remember or a second factor can take a while, and the
      // other instances of this tab must keep waiting rather than start flows of their own.
      if (Date.now() - renewedAt >= AUTH_FLOW_RENEW_MS) {
        renewedAt = Date.now();
        this.coordinator.renewFlow(expectedState);
      }
      if (popup.closed) {
        stopWatching();
        popup = undefined;
        if (!done) {
          // the agent closed the sign-in window. nothing is coming, so say so and offer a way
          // back instead of leaving them looking at a spinner. let go of the flow too, so the
          // other instances of this tab do not sit out its whole lease.
          this.coordinator.abandonFlow(expectedState);
          this.promptPopupInterrupted(
            authorizationUrl,
            flowId,
            "popup-interrupted",
            "Sign-in was interrupted",
            "authentication-popup-closed"
          );
        }
        return;
      }
      try {
        // throws while the popup is still on the identity provider's origin
        const href = popup.location?.href;
        //  we got redirected back to auvious
        if (href && isOurResponse(href)) {
          // the location is read above, before closing. reading it after close() throws,
          // and the catch below would silently leave us watching a window that is gone.
          popup.close();
          popup = undefined;
          takeOver(href);
        } else if (href && isAuthorizationResponse(href)) {
          // our window name was taken over by another instance's flow and this popup is now
          // serving that one. it is not ours to close, and it is never going to answer us.
          stopWatching();
          popup = undefined;
          this.coordinator.abandonFlow(expectedState);
          this.promptPopupInterrupted(
            authorizationUrl,
            flowId,
            "popup-interrupted",
            "Sign-in was interrupted",
            "authentication-popup-closed"
          );
        }
      } catch (ex) {}
    }, 1000);
  }

  /** how many times this instance has already come back from the identity provider */
  private attemptsInUrl(): number {
    try {
      const count = Number(
        new URLSearchParams(window.location.search).get(PARAM_AUTH_ATTEMPT)
      );
      return Number.isFinite(count) && count > 0 ? count : 0;
    } catch {
      return 0;
    }
  }

  /** a full document navigation. a seam, so that the specs can see where we go */
  private navigate(url: string) {
    window.location.replace(url);
  }

  private async redirectForCode(queryParams: URLSearchParams) {
    if (!this.coordinator.consumeAttempt()) {
      // asking again is what ends up answered with "too many redirects"
      this.notifyTooManyAttempts(queryParams);
      return;
    }
    const url = await this.getAuthorizationUrl(queryParams);
    window.location.replace(url.toString());
    // delay here, so that noone manages to route elsewhere
    await delay(5000);
  }

  /**
   * drop what this instance keeps about signing in, and nothing else. session storage also holds
   * the agent's devices, the cobrowse session, the genesys environment - and, when the
   * application runs several times in one tab, the other instances' access tokens and the flow
   * one of them may be running right now.
   */
  private clearAuthState(code: string, state: string) {
    try {
      sessionStore.removeItem(OAUTH2_STATE);
      sessionStore.removeItem(KEY_REDIRECT_URI);
      if (code) {
        sessionStore.removeItem(code);
      }
    } catch {}
    this.coordinator.forgetIssuedState(state);
    this.coordinator.abandonFlow(state);
  }

  private storeRedirectPath() {
    // keep the current page so that when we come back from the redirect, to move to that page
    // this way we always redirect to one specific page and from that page to whatever the target url was
    sessionStore.setItem(KEY_REDIRECT_URI, window.location.pathname);
  }

  private getRedirectPath() {
    // Always redirecto to same page, a guard at that page checks for a sessionStorage key to redirect accordingly
    return this.options.redirectUri;
    // return window.location.origin + window.location.pathname;
  }

  private async getAccessToken(
    code: string,
    reusableCredential?: { param: string; value: string }
  ): Promise<ITokenResponse> {
    const params = new URLSearchParams();
    params.set("client_id", this.options.clientId);
    params.set("grant_type", "password");

    if (reusableCredential) {
      // no authorization code to redeem, and so no redirect uri to redeem it against: the
      // credential stands in for both.
      params.set(reusableCredential.param, reusableCredential.value);
    } else {
      params.set("code", code);
      params.set("redirect_uri", this.getRedirectPath());
    }

    this.options.hydratePasswordParams?.(params);

    const options = {
      headers: new HttpHeaders().set(
        "Content-Type",
        "application/x-www-form-urlencoded"
      )
    };

    this.options.hydrateHeaders?.(options);
    // console.log("headers: ", options.headers);

    // try {
    const response = await firstValueFrom(
      this.httpClient.post<ITokenResponse>(
        "/security/oauth/token",
        params.toString(),
        {
          ...options,
          observe: "response" as const // Ensure the response includes headers
        }
      )
    );

    const tokenResponse = response.body; // Extract the body

    this.options.onHttpResponse?.(response);

    // a token is kept under the code that bought it. a session established with a reusable
    // credential has no such key - storing it under "undefined" would put every instance of the
    // tab on one entry, and the refresh tokens in it are single use.
    if (this.persistAccessToken && code) {
      // Save access token
      OAuth2TokenHelper.saveToken(code, tokenResponse);
    }
    this.options.onTokenResponse?.(tokenResponse);

    return tokenResponse;
  }

  private refreshTokenFn(
    code
  ): (refreshToken: string) => Promise<IJwtTokenResponse> {
    return async (refreshToken: string): Promise<IJwtTokenResponse> => {
      const params = new URLSearchParams();
      params.set("client_id", this.options.clientId);
      params.set("grant_type", "refresh_token");
      params.set("refresh_token", refreshToken);

      const options = {
        headers: new HttpHeaders().set(
          "Content-Type",
          "application/x-www-form-urlencoded"
        )
      };

      const tokenResponse = await firstValueFrom(
        this.httpClient.post<ITokenResponse>(
          "/security/oauth/token",
          params.toString(),
          options
        )
      );

      if (this.persistAccessToken && code) {
        // Save access token
        OAuth2TokenHelper.saveToken(code, tokenResponse);
      }
      if (!!this.options.onTokenResponse) {
        this.options.onTokenResponse(tokenResponse);
      }

      return tokenResponse;
    };
  }
}
